import {useDispatch} from 'react-redux'
import {useState,useRef,useEffect,useCallback} from 'react'
import { Line,Scatter } from 'react-chartjs-2'
import HowTo from './HowTo'
import Webcam from "react-webcam"
import useCountDown from "react-countdown-hook"
import {capitalize} from '../../util/util'
import useCurrentUser from '../../hooks/useCurrentUser'
import useWindowSize from '../../hooks/useWindowSize'
import * as poseDetection from '@tensorflow-models/pose-detection'
import * as tf from '@tensorflow/tfjs-core'
// Register one of the TF.js backends.
import '@tensorflow/tfjs-backend-webgl'
import {
	isWebview,
	getThighValgusAngle,
	arrAvg,
	appendAvg,
	depthScoreByKneeAngle,
	projectKeypointsUntoAxis,
	roundDecimal,
	singleLegKneeScore,
	calculateThighLength,
	calculateCalfLength,
	normalizeX,
	repConsistency,
	tempoConsistency,
	depthScore,
	extractRepSlices,
	nonMonotonicIndexes,
	argMin,
	argMax,
	getKneeCaveTimeMap,
	restConsistency,
	kneeArticulationConsistency,
	centerLineConsistency,
	repsCenterLineScore,
	chestCenter,
	hipCenter,
	useAnimationFrame,
	poseFemurLength,
	poseHeight,
	balancingSide,
	calculateCenterStatus,
	calculateShoulderTilt,
	calculateHipTilt,
	tiltScore,
	kneeHeightScore,
	nearest,
	countPerRepHandsOffHips,
	singleLegSquatBalanceScore
} from './util'
import {
	removeLeaderboardRecord,
	editLeaderboardRecord,
	addLeaderboardRecord,
	setMetricCreator,
	setPoses,
	setShow
} from './slice'
import styles from './AIDemo.module.css'
import Guides from './Guides'
import {dateToYMD} from '../../util/datetime'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Leaderboard from './Leaderboard'
import Indicators from './Indicators'
import Poses from './Poses'
import KeypointLogViewer from './KeypointLogViewer'
import testSingleLegData from './single-leg-test-data.json'

const SCORE_THRESHOLD = 0.35

const detectorConfig = {
	modelType: poseDetection.movenet.modelType.SINGLEPOSE_THUNDER,
	enableTracking: true,
	trackerType: poseDetection.TrackerType.BoundingBox,
}

const KeyRepMetricsTable = ({reps})=>{
	return (
		<Table striped bordered>
			<thead>
				<tr>
					<th>Metric</th>
					<th>Rep 1</th>
					<th>Rep 2</th>
					<th>Rep 3</th>
					<th>Average</th>
				</tr>
			</thead>
			<tbody>
				{reps.map(rep=>(
					<tr>
						<td>{rep.metric}</td>
						{rep.values.map(({v,color})=>(
							<td style={{color:color||'#000'}}>{v}</td>
						))}
					</tr>
				))}
			</tbody>
		</Table>
	)
}

const SingleLegSquatDemo = ()=>{
	const dispatch = useDispatch()
	const [windowWidth,windowHeight] = useWindowSize()

	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const ratio = 1//windowWidth/windowHeight

	const [detector,setDetector] = useState(null)
	const [showHowTo,setShowHowTo] = useState(false)
	const [showResults,setShowResults] = useState(false)
	const [showLeaderboard,setShowLeaderboard] = useState(false)
	const testVidRef = useRef(null)
	const testVidLoadedRef = useRef(false)

	const canvasRef = useRef(null)

	const goalCount = 3
	const countRef = useRef({
		left:0,
		right:0
	})
	const leftCountSpanRef = useRef(null)
	const rightCountSpanRef = useRef(null)

	const [timeLeft,{start:startCoundown,reset:resetCountdown}] = useCountDown(3000, 1000)
	const countingDownRef = useRef(false)

	const initializeGuides = async ()=>{
		const ctx = canvasRef.current.getContext("2d")
		ctx.drawImage(webcamRef.current.video,0,0,320,320/ratio)
		const poses = await detector.estimatePoses(canvasRef.current)
		if (poses.length === 0) return
		const keypoints = poses[0].keypoints
		//const nose = keypoints[0]
		//using left hand for now
		const nose = keypoints[0]
		resultsRef.current = emptyResults()
		countRef.current = {left:0,right:0}
		squatStateRef.current = 'balancing'
		leftCountSpanRef.current.innerText = countRef.current.left
		rightCountSpanRef.current.innerText = countRef.current.right

		squatStateSpanRef.current.innerText = stateDisplayTextMap['balancing']
		
		const side = balancingSide(keypoints)
		const thighLength = calculateThighLength(keypoints,side)
		const calfLength = calculateCalfLength(keypoints,side)
		const legLength = thighLength + calfLength
		const bentLegLength = Math.sqrt(
			Math.pow(thighLength,2) +
			Math.pow(calfLength,2) -
			2*thighLength*calfLength*Math.cos(150/180*Math.PI)
		)
		const diff = legLength - bentLegLength 


		const squatBottomY = nose.y + diff
		//max of two ankles with 1/25 of pose height as tolerance
		const ankleBottomY = Math.max(keypoints[15].y,keypoints[16].y) - poseHeight(keypoints)/25
		guidesRef.current = {
			...guidesRef.current,
			height:poseHeight(keypoints),
			squatStartY:nose.y+poseHeight(keypoints)*.03,
			idealChestY:chestCenter(keypoints,'y') + diff,
			leftKneeX:320-keypoints[13].x,
			rightKneeX:320-keypoints[14].x,
			squatBottomY,
			ankleBottomY
		}
		setShowGuides(true)
	}

	useEffect(()=>{
		if(!overlayRef.current||!countingDownRef.current) return
		if(timeLeft===0&&countingDownRef.current){
			countingDownRef.current = false
			initializeGuides()
			overlayRef.current.innerText = 'GO!'
			setTimeout(()=>{setShowOverlay(false)},1500)
		}else{
			overlayRef.current.innerText = Math.ceil(timeLeft/1000)
		}
	},[timeLeft,countingDownRef])

	//stores results from squat analysis
	const emptyResults = ()=>({
		keypointLog:[],
		left:{
			reps:{
				started:[],
				ended:[]
			},
			kneeCaveMap:{
				left:[],
				right:[]
			},
			valgusAngle:[],
			kneeAngle:[],
			chestPosition:[],
			valgus:[],
			shoulderTilt:[],
			hipTilt:[],
			handsOffHips:[]
		},
		right:{
			reps:{
				started:[],
				ended:[]
			},
			kneeCaveMap:{
				left:[],
				right:[]
			},
			valgusAngle:[],
			kneeAngle:[],
			chestPosition:[],
			valgus:[],
			shoulderTilt:[],
			hipTilt:[],
			handsOffHips:[]
		}
	})

	const [results,setResults] = useState(testSingleLegData)//useState(emptyResults())//
	const resultsRef = useRef(testSingleLegData)

	const posesRef = useRef([])
	const [showGuides,setShowGuides] = useState(false)
	const [showOverlay,setShowOverlay] = useState(false)
	const overlayRef = useRef(null)
	const guidesRef = useRef({
		"height": 265.77991748160304,
		"squatStartY": 50.711826575715534,
		"center": {
			"left": 0,
			"right": 0
		},
		"idealChestY": 83.8019729595855,
		"leftKneeX": 130.0874745556327,
		"rightKneeX": 161.94527206853908,
		"bottomY": 0,
		"thighLength": 61.78533984140623,
		"calfLength": 58.334537907467265,
		"squatBottomY": 46.790808960639026,
		"ankleBottomY": 267.46161835214605
	})

	const [name,setName] = useState('')
	const [email,setEmail] = useState('')

	useEffect( async ()=>{
		setDetector(
			await poseDetection.createDetector(
				poseDetection.SupportedModels.MoveNet,
				detectorConfig
			)
		)
	},[])

	//console.log('render')
	//reference to if we have initialized the squat guides
	const initializedRef = useRef(false)
	const trackResultsRef = useRef(false)
	const webcamRef = useRef(null)

	const stateDisplayTextMap = {
		standing:'Standing',
		squatting:'Squatting',
		waiting:'Waiting',
		balancing:'Balancing',
		setup:'Get Ready',
		initialize:'Position Yourself'
	}

	const initState = 'waiting'
	const squatStateRef = useRef(initState)
	const squatStateSpanRef = useRef(null)
	//predicates 
	const handsOffHips = (keypoints,height)=>{
		try {
			const leftWrist = keypoints[9]
			const rightWrist = keypoints[10]
			const leftHip = keypoints[11]
			const rightHip = keypoints[12]
			const w = 0.12*height
			const h = 0.2*height

			const leftWristOutsideBox = (
				//outside of x bounds
				leftWrist.x <= leftHip.x-w-0.03*height ||
				leftWrist.x >= leftHip.x-0.03*height ||
				//outside of y bounds
				leftWrist.y <= leftHip.y-h ||
				leftWrist.y >= leftHip.y
			)

			const rightWristOutsideBox = (
				//outside of x bounds
				rightWrist.x <= (rightHip.x)+0.03*height ||
				rightWrist.x >= rightHip.x+w+0.03*height ||
				//outside of y bounds
				rightWrist.y <= rightHip.y-h ||
				rightWrist.y >= rightHip.y
			)
			return (leftWristOutsideBox||rightWristOutsideBox)
		}catch(err){
			console.error(err)
		}
	}

	const infer = useCallback( async () => {
		//console.log(store.getState())
		//map from current state to trigger for transitioning to next
		//state
		const onTransition = (t)=>{
			if (!squatStateSpanRef.current) return
			squatStateSpanRef.current.innerText = stateDisplayTextMap[squatStateRef.current]
		}
		
		//runs on every inference to log data continuously (eg nose & knee positions)
		const squatDataLogger = (keypoints,t)=>{
			const side = balancingSide(keypoints)
			const nose = keypoints[0]
			const leftKnee = keypoints[13]
			const rightKnee = keypoints[14]

			const leftAnkle = keypoints[15]
			const rightAnkle = keypoints[16]

			const bentThighLength = calculateThighLength(
				projectKeypointsUntoAxis(keypoints,'y'),
				side
			)

			const valgusAngle = getThighValgusAngle(side,keypoints)

			//only log valid knee angles 
			//depending on errors in inferencing sometimes the calculation will result
			//in undefined values
			if(Boolean(valgusAngle)){
				resultsRef.current[side].valgusAngle.push({
					angle:valgusAngle,
					t,
				})
			}

			//theta = acos{ (a^2 + b^2 - c^2)/(2ab) }*180/pi
			const kneeAngle = Math.acos(
				bentThighLength/guidesRef.current.thighLength
			)*180/Math.PI

			//only log valid knee angles 
			//depending on errors in inferencing sometimes the calculation will result
			//in undefined values
			if(Boolean(kneeAngle)){
				resultsRef.current[side].kneeAngle.push({
					angle:kneeAngle,
					t,
				})
			}

			resultsRef.current[side].chestPosition.push({
				x:320-chestCenter(keypoints),
				y:nose.y,
				t
			})

			//valgus is measured from the medial part of the big toe 
			//so we need to add an offset for this. This offset is calculated
			//assuming a heal to medial distance of 1.75 inches. 
			//We create a unitless value 1.75/69 (ie percent of total height) 
			//then convert this to a value based on the estimated pose height
			const toeOffset = 1.75/69*guidesRef.current.height

			const valgus = (
				side === 'left' 
				? leftKnee.x - leftAnkle.x - toeOffset
				: rightAnkle.x - rightKnee.x - toeOffset
			)

			resultsRef.current[side].valgus.push({valgus,t})

			resultsRef.current[side].hipTilt.push({
				angle:calculateHipTilt(keypoints),
				t
			})
			resultsRef.current[side].shoulderTilt.push({
				angle:calculateShoulderTilt(keypoints),
				t
			})
			
			resultsRef.current[side].handsOffHips.push({
				offHips:handsOffHips(keypoints,guidesRef.current.height),
				t
			})
		}

		//These are triggers that get fired if in the current state
		//For example: if the current state is standing, then only the 
		//trigger for standing gets fired 
		const squatTransitionTriggers = {
			initialize:(keypoints,t)=>{
				//when elbow and wrist keypoints go above shoulders
				//then transition into standing

				//left arm keypoints
				const lWrist = keypoints[10]
				const lElbow = keypoints[8]
				const lShoulder = keypoints[6]

				const lArmUp = (
					lWrist.y < lShoulder.y &&
					lElbow.y < lShoulder.y
				)

				//right arm keypoints
				const rWrist = keypoints[9]
				const rElbow = keypoints[7]
				const rShoulder = keypoints[5]

				const rArmUp = (
					rWrist.y < rShoulder.y &&
					rElbow.y < rShoulder.y
				)

				if(lArmUp&&rArmUp){
					//initialize the thight/calf lengths as
					//the average of both legs
					guidesRef.current.thighLength = (
						calculateThighLength(keypoints,'left') +
						calculateThighLength(keypoints,'right')
					)/2
					guidesRef.current.calfLength = (
						calculateCalfLength(keypoints,'left') +
						calculateCalfLength(keypoints,'right')
					)/2
					squatStateRef.current = 'setup'
					overlayRef.current.innerText = 'Get Ready'
					setShowOverlay(true)
				}
			},
			setup:(keypoints,t)=>{
				//when elbow and wrist keypoints go above shoulders
				//then transition into standing

				//if wrist is in this radius then hands are considered "on hip"
				const hipRadius = 30
				//if one ankle is this far above the other then it is considered lifted
				const liftTolerance = 10

				//left arm keypoints
				const lWrist = keypoints[9]
				const lElbow = keypoints[7]
				const lShoulder = keypoints[5]
				const lHip = keypoints[11]
				const leftAnkle = keypoints[15]
				const leftKnee = keypoints[13]


				//right arm keypoints
				const rWrist = keypoints[10]
				const rElbow = keypoints[8]
				const rShoulder = keypoints[6]
				const rHip = keypoints[12]
				const rightAnkle = keypoints[16]
				const rightKnee = keypoints[14]
				
				//Propositions
				const leftAnkleLifted = leftAnkle.y <= rightAnkle.y - liftTolerance

				const leftHandOnHip = (
					(Math.abs(lWrist.y - lHip.y) <= hipRadius) &&
					(Math.abs(lWrist.x - lHip.x) <= hipRadius)
				)

				const rightAnkleLifted = rightAnkle.y <= leftAnkle.y - liftTolerance

				const rightHandOnHip = (
					Math.abs(rWrist.y - rHip.y) <= hipRadius &&
					Math.abs(rWrist.x - rHip.x) <= hipRadius
				)

				//only trigger setup if all keypoints have sufficient score
				const keypointsInView = (
					lWrist.score     >= SCORE_THRESHOLD &&
					lHip.score       >= SCORE_THRESHOLD &&
					leftAnkle.score  >= SCORE_THRESHOLD &&
					rWrist.score     >= SCORE_THRESHOLD &&
					rHip.score       >= SCORE_THRESHOLD &&
					rightAnkle.score >= SCORE_THRESHOLD
				)

				if (
					!countingDownRef.current &&
					keypointsInView &&
					leftHandOnHip &&
					rightHandOnHip && 
					(
						leftAnkleLifted ||
						rightAnkleLifted
					) 
				) {
					startCoundown()
					countingDownRef.current = true
					setShowOverlay(true)
				}
			},
			standing:(keypoints,t)=>{
				const leftAnkle = keypoints[15]
				const rightAnkle = keypoints[16]
				//if one ankle is this far above the other then it is considered lifted
				const liftTolerance = 5
				const leftAnkleLifted = leftAnkle.y <= rightAnkle.y - liftTolerance
				const rightAnkleLifted = rightAnkle.y <= leftAnkle.y - liftTolerance
				if(leftAnkleLifted||rightAnkle){
					squatStateRef.current = 'balancing'
				}
			},
			squatting:(keypoints,t)=>{
				//if one ankle is this far above the other then it is considered lifted
				const liftTolerance = 10
				const nose = keypoints[0]
				const leftAnkle = keypoints[15]

				const rightAnkle = keypoints[16]

				const leftAnkleLifted = leftAnkle.y <= rightAnkle.y - liftTolerance
				const rightAnkleLifted = rightAnkle.y <= leftAnkle.y - liftTolerance

				//if in balancing state transition to stand 
				//when one both ankles below threshold
				if ( !leftAnkleLifted && !rightAnkleLifted ) {
					squatStateRef.current = 'standing'
				}
				if ( nose.y <= guidesRef.current.squatStartY ) {
					squatStateRef.current = 'balancing'
					const side = balancingSide(keypoints)
					resultsRef.current[side].reps.ended.push(t)
					if(rightAnkleLifted){
						countRef.current.left += 1
					}else{
						countRef.current.right += 1
					}


					leftCountSpanRef.current.innerText = countRef.current.left
					rightCountSpanRef.current.innerText = countRef.current.right
					if ( 
						countRef.current.left >= goalCount && 
						countRef.current.right >= goalCount 
					) {
						console.log(resultsRef.current)
						try {
							resultsRef.current.left.kneeCaveMap.left = getKneeCaveTimeMap(
								resultsRef.current.left.reps,
								resultsRef.current.left.leftKneePosition,
								resultsRef.current.left.chestPosition
							)
							resultsRef.current.right.kneeCaveMap.right = getKneeCaveTimeMap(
								resultsRef.current.right.reps,
								resultsRef.current.right.rightKneePosition,
								resultsRef.current.right.chestPosition
							)
						} catch (err) {
							console.error(err)
						}
						overlayRef.current.innerText = 'Done. Processing Results'
						setShowOverlay(true)
						setTimeout(()=>{
							setShowOverlay(false)
							cancelInferencing()
							setShowGuides(false)
							setResults(resultsRef.current)
							setShowResults(true)
							//testVidRef.current.pause()
							trackResultsRef.current = false
							//testVidRef.current.currentTime = 0
						},2000)
					}
				}
			},
			switching:(keypoints,t)=>{
				const leftAnkle = keypoints[15]
				const rightAnkle = keypoints[16]
				//if in standing state transition to balancing 
				//when one ankle goes above the threshold

				const leftAnkleLifted = leftAnkle.y <= guidesRef.current.bottomY
				const rightAnkleLifted = rightAnkle.y <= guidesRef.current.bottomY
				const currentBalanceFoot = resultsRef.current.balanceFoot
				if ( 
					//if was balancing on left foot and is now balancing on right foot
					( leftAnkleLifted && currentBalanceFoot === 'left' ) || 
					//if was balancing on right foot and is now balancing on left foot
					( rightAnkleLifted && currentBalanceFoot === 'right' )
				) {
					squatStateRef.current = 'balancing'
					if(leftAnkleLifted){
						resultsRef.current.balanceFoot = 'right'
						resultsRef.current.right.start = t
					}else {
						resultsRef.current.balanceFoot = 'left'
						resultsRef.current.left.start = t	
					}
				}
			},
			balancing:(keypoints,t)=>{
				//if one ankle is this far above the other then it is considered lifted
				const liftTolerance = 10
				const side = balancingSide(keypoints)
				const leftAnkle = keypoints[15]

				const rightAnkle = keypoints[16]

				const leftAnkleLifted = leftAnkle.y <= rightAnkle.y - liftTolerance
				const rightAnkleLifted = rightAnkle.y <= leftAnkle.y - liftTolerance

				//if in balancing state transition to stand 
				//when one both ankles below threshold
				if ( !leftAnkleLifted && !rightAnkleLifted ) {
					squatStateRef.current = 'standing'
					//resultsRef.current[resultsRef.current.balanceFoot].end = t
				}
				const nose = keypoints[0]
				if ( nose.y >= guidesRef.current.squatStartY ) {
					const side = balancingSide(keypoints)
					resultsRef.current[side].reps.started.push(t)
					squatStateRef.current = 'squatting'
				}
			},
		}

		if (!webcamRef.current||!detector) {
			//console.log('Waiting for dependencies')
		} else {
			const ctx = canvasRef.current.getContext("2d")
			ctx.drawImage(webcamRef.current.video,0,0,320,320/ratio)
			if (!initializedRef.current||!trackResultsRef.current) {
				console.log('Not initialized')
				return
			}
			try {
				//console.log(`inferencing`)
				const poses = await detector.estimatePoses(canvasRef.current)
				posesRef.current = poses
				dispatch(setPoses(poses.map(p=>{
					p.keypoints = p.keypoints.map(k=>({...k,x:320-k.x}))
					return p
				})))
				if ( poses.length > 0 ) {
					const keypoints = poses[0].keypoints
					const nose = keypoints[0]
					const t = Date.now()
					resultsRef.current.keypointLog.push({keypoints,t})
					if ( nose.score >= SCORE_THRESHOLD ) {
						if ( !['setup','initialize'].includes(squatStateRef.current) ) squatDataLogger(keypoints,t) 
						const transitionTrigger = squatTransitionTriggers[squatStateRef.current]
						if (transitionTrigger) {
							transitionTrigger(keypoints,t)
							onTransition(t)
						}
					}
				}
			} catch(err) {
				//console.log(err)
			}
		}
	},[detector,webcamRef,guidesRef])

	const {
		cancel:cancelInferencing,
		start:startInferencing
	} = useAnimationFrame(infer)

	const [started,setStarted] = useState(false)
	const startDemo = async ()=>{
		setStarted(true)
		cancelInferencing()
		resetCountdown()
		setShowGuides(false)

		resultsRef.current = emptyResults()
		countRef.current = {left:0,right:0}
		leftCountSpanRef.current.innerText = countRef.current.left
		rightCountSpanRef.current.innerText = countRef.current.right
		squatStateSpanRef.current.innerText = stateDisplayTextMap['initialize']

		initializedRef.current = true
		squatStateRef.current = 'initialize'
		resultsRef.current = emptyResults()
		trackResultsRef.current=true
		//testVidRef.current.play()
		startInferencing()
		//run a single inference to load model unto GPU
		const ctx = canvasRef.current.getContext("2d")
		ctx.drawImage(webcamRef.current.video,0,0,320,320/ratio)
		const poses = await detector.estimatePoses(canvasRef.current)
	}

	const restartDemo = ()=>{
		setShowResults(false) 
		setShowGuides(false)
		resetCountdown()
		cancelInferencing()
		setStarted(false)
		countingDownRef.current = false
		resultsRef.current = emptyResults()
		countRef.current = {left:0,right:0}
	}

	const stopDemo = ()=>{
		setShowOverlay(false)
		setShowGuides(false)
		countingDownRef.current = false
		resetCountdown()
		cancelInferencing()
		setStarted(false)
		resultsRef.current = emptyResults()
		countRef.current = {left:0,right:0}
		leftCountSpanRef.current.innerText = countRef.current.left
		rightCountSpanRef.current.innerText = countRef.current.right
		squatStateSpanRef.current.innerText = stateDisplayTextMap[initState]
	}

	const handleStartTracking = ()=>{
		resultsRef.current = emptyResults()
		trackResultsRef.current=true
		startInferencing()
	}

	const handleReset = ()=>{
		cancelInferencing()
	}

	const [saving,setSaving] = useState(false)

	const handleSave = ()=>{
		setSaving(true)``
		dispatch(addLeaderboardRecord({
			contact_email:email,
			rep_pace:repConsistency(results.reps),
			rest_pace:restConsistency(results.reps),
			center_balance:centerLineConsistency(results.chestPosition),
			/*
			left_knee_balance:kneeArticulationConsistency(
				results.reps,
				results.leftKneePosition,
				results.kneeCaveMap.left
			),*/
			/*
			right_knee_balance:kneeArticulationConsistency(
				results.reps,
				results.rightKneePosition,
				results.kneeCaveMap.right
			),*/
			name:name,
		})).then(()=>{
			setSaving(false)
			setName('')
			setEmail('')
		})
	}

	const handleAddToProgress = ()=>{

		const metrics = [
			{
				name:'Left Max Knee Flexion Angle',
				//degrees
				unit_id:10,
				value:arrAvg(
					extractRepSlices(results.left.reps,results.left.kneeAngle)
					.map( d => Math.max( ...d.map( d => d.angle ) ) )
				).toFixed(1)
			},{
				name:'Right Max Knee Flexion Angle',
				//degrees
				unit_id:10,
				value:arrAvg(
					extractRepSlices(results.right.reps,results.right.kneeAngle)
					.map( d => Math.max( ...d.map( d => d.angle ) ) )
				).toFixed(1)
			},{
				name:'Left Max Valgus Angle',
				//degrees
				unit_id:10,
				value: arrAvg(
					extractRepSlices(results.left.reps,results.left.valgusAngle)
					.map( d => Math.max( ...d.map( d => d.angle ) ) )
				).toFixed(1)
			},{
				name:'Right Max Valgus Angle',
				//degrees
				unit_id:10,
				value:arrAvg(
					extractRepSlices(results.right.reps,results.right.valgusAngle)
					.map( d => Math.max( ...d.map( d => d.angle ) ) )
				).toFixed(1)
			},{
				name:'Left Max Hip Drop',
				//inches
				unit_id:6,
				value:arrAvg(
					extractRepSlices(results.left.reps,results.left.hipTilt)
					.map( d => Math.max( ...d.map( d => Math.abs(d.angle) ) ) )
					//map to inch estimate
					//18 is estimated waist WIDTH (ie inches from side to side)
					.map( d => Math.sin(d/180*Math.PI)*18 )
				).toFixed(1)
			},{
				name:'Right Max Hip Drop',
				//inches
				unit_id:6,
				value:arrAvg(
					extractRepSlices(results.right.reps,results.right.hipTilt)
					.map( d => Math.max( ...d.map( d => Math.abs(d.angle) ) ) )
					//18 is estimated waist WIDTH (ie inches from side to side)
					//map to inch estimate
					.map( d => Math.sin(d/180*Math.PI)*18 )
				).toFixed(1)
			},{
				name:'Left Knee Deviation',
				//inches
				unit_id:6,
				value:arrAvg(
					extractRepSlices(results.left.reps,results.left.valgus)
					.map( d => Math.max( ...d.map( d => d.valgus/guidesRef.current.height*69 ) ) )
				).toFixed(1)
			},{
				name:'Right Knee Deviation',
				//inches
				unit_id:6,
				value:arrAvg(
					extractRepSlices(results.right.reps,results.right.valgus)
					.map( d => Math.max( ...d.map( d => d.valgus/guidesRef.current.height*69 ) ) )
				).toFixed(1)
			},{
				name:'Hands Off Hips Count (Left)',
				unit_id:1,
				value:arrAvg(
					countPerRepHandsOffHips(results.left.reps,results.left.handsOffHips)
				).toFixed(1)
			},{
				name:'Hands Off Hips Count (Right)',
				unit_id:1,
				value:arrAvg(
					countPerRepHandsOffHips(results.right.reps,results.right.handsOffHips)
				).toFixed(1)
			}
		]

		dispatch(setMetricCreator({
			show:true,
			category:'AI Single Leg Squat Assessment',
			date:dateToYMD(new Date()),
			metrics,
			aiLog:JSON.stringify(results)
		}))
	}

	const averageScore = (side)=>{
		const scores = [
			depthScoreByKneeAngle(
				results[side].reps,
				results[side].kneeAngle
			),
			singleLegKneeScore(results[side].reps,results[side].valgus),
			singleLegSquatBalanceScore(results[side].reps,results[side].handsOffHips),
			tiltScore(
				results[side].hipTilt.map(p=>({
					x:p.t - results[side].hipTilt[0].t,
					y:p.angle,
					status:p.status
				}))
			)
		]
		const average = scores.reduce(
			(carry,score)=>carry+score,
			0
		)/scores.length
		return average
	}

	return (
		<div className={styles.AIDemoWidget}>
			<div ref={overlayRef} style={{
				position:'absolute',
				width:'100vw',
				height:'100vh',
				background:'rgba(0,0,0,0.2)',
				color:'rgb(50,255,50)',
				display:showOverlay?'flex':'none',
				justifyContent:'center',
				fontWeight:600,
				alignItems:'center',
				fontSize:'120px',
				zIndex:3
			}}/>
			{!showResults&&
				<div className="m-auto">
					<div style={{
						position:'relative'
					}}>
						<svg style={{
							position:'absolute',
							top:0,left:0,
							zIndex:2,
							overflow:'visible',
							width: "100vw",
							height: "100vh",
						}}>
							{started&&
								<circle
									cx={webcamRef.current.video.getBoundingClientRect().width/2}
									cy={webcamRef.current.video.getBoundingClientRect().height/2}
									r="8"
									fill="rgb(100,255,100)"
								/>
							}
							{showGuides&&<Guides 
								guides={guidesRef.current} 
								ratio={1}
								scale={webcamRef.current.video.getBoundingClientRect().width/320}
							/>}
							{Boolean(webcamRef.current?.video)&&
								<>
									<Poses 
										scale={webcamRef.current.video.getBoundingClientRect().width/320} 
										threshold={SCORE_THRESHOLD}
									/>
									<Indicators scale={webcamRef.current.video.getBoundingClientRect().width/320}/>
								</>
							}
						</svg>
					</div>
					
					{/*<video 
						style={{
							height:`${windowHeight}px`,
							width:`${windowWidth}px`,
							display:'block'
						}}
						onLoadedData={()=>{testVidLoadedRef.current = true}}
						crossorigin='anonymous' 
						ref={testVidRef} 
						muted loop
						src="http://localhost/assets/videos/out.mp4"
					/>*/}
					<Webcam 
						style={{
							height: windowWidth >  windowHeight ? '100vh' : 'auto',
							width:  windowWidth >= windowHeight ? 'auto'  : '100vw',
							display:'block',
							margin:'auto'
						}}
						ref={webcamRef}
						mirrored
						videoConstraints={{
							width: 720,
							height:720,
							facingMode:"user",
							deviceId:"c141e16a643707300abde9eaae8a522f531793da15be147c3e1d7f40dcdfaea7"
						}}
					/>
					<canvas 
						style={{display:'none'}} 
						width={320} height={320} 
						ref={canvasRef}
					/>
					<div style={{
						position:'absolute',
						top:'4px',
						right:'4px',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						borderRadius:'8px',
						zIndex:3,
						width:'200px'
					}}>
						<div className="d-flex align-items-stretch justify-content-center mb-2" style={{fontSize:'40px'}}>
							<div>
								<span ref={leftCountSpanRef}>{countRef.current.left}</span>/{goalCount}
								<small style={{fontSize:'14px',margin:0}} className="text-muted d-block text-center">Left</small>
							</div>
							<div style={{width:'1px',background:'#ccc',margin:'0 16px'}}/>
							<div>
								<span ref={rightCountSpanRef}>{countRef.current.right}</span>/{goalCount}
								<small style={{fontSize:'14px',margin:0}} className="text-muted d-block text-center">Right</small>
							</div>
						</div>
						<div className="d-flex justify-content-between">
							<small className="mr-2" ref={squatStateSpanRef}>Waiting</small>
							<small className={Boolean(detector)?'text-success':'text-muted'}>
								{Boolean(detector)?'AI Downloaded':'Downloading AI...'}
							</small>
						</div>
					</div>
					<div style={{
						position:'absolute',
						bottom:0,
						left:0,
						display:'flex',
						flexWrap:'wrap',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						zIndex:3
					}}>
						<Button
							className="mr-2"
							variant="outline-primary"
							onClick={()=>{
								setShowHowTo(p=>!p)
							}}
						>
							<i className="fa fa-question-circle" /> How To
						</Button>
						{!started&&
							<Button className="mr-2" onClick={startDemo} variant="outline-success">
								Start
							</Button>
						}
						{started&&
							<Button className="mr-2" onClick={stopDemo} variant="outline-danger">
								Stop
							</Button>
						}
						{!isWebview()&&
							<Button 
								className="mr-2"
								onClick={()=>{
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
					</div>
				</div>
			}
			{showResults&&
				<div style={{
					width:'100vw',
					height:'100vh',
					overflow:'auto',
					padding:'16px'
				}}>
					<div style={{
						fontSize:'32px',
						textAlign:'center',
						marginTop:'44px',
						marginBottom:'16px'
					}}>
						Results Analysis
					</div>
					<KeyRepMetricsTable
						reps={[
							{
								metric:'Left Max Knee Flexion Angle',
								values: (
									appendAvg(
										extractRepSlices(results.left.reps,results.left.kneeAngle)
										.map( d => Math.max( ...d.map( d => d.angle ) ) )
									).map( d => ({
										v:`${d.toFixed()}${String.fromCharCode(176)}`,
										color:d>=20?'green':'red'
									}) )
								)
							},{
								metric:'Right Max Knee Flexion Angle',
								values:(
									appendAvg(
										extractRepSlices(results.right.reps,results.right.kneeAngle)
										.map( d => Math.max( ...d.map( d => d.angle ) ) )
									).map( d => ({
										v:`${d.toFixed()}${String.fromCharCode(176)}`,
										color:d>=20?'green':'red'
									}) )
								)
							},{
								metric:'Left Max Valgus Angle',
								values: (
									appendAvg(
										extractRepSlices(results.left.reps,results.left.valgusAngle)
										.map( d => Math.max( ...d.map( d => d.angle ) ) )
									).map( d => ({
										v:`${d.toFixed(1)}${String.fromCharCode(176)}`
									}) )
								)
							},{
								metric:'Right Max Valgus Angle',
								values:(
									appendAvg(
										extractRepSlices(results.right.reps,results.right.valgusAngle)
										.map( d => Math.max( ...d.map( d => d.angle ) ) )
									).map( d => ({
										v:`${d.toFixed(1)}${String.fromCharCode(176)}`
									}) )
								)
							},{
								metric:'Left Max Hip Drop',
								values:(
									appendAvg(
										extractRepSlices(results.left.reps,results.left.hipTilt)
										.map( d => Math.max( ...d.map( d => Math.abs(d.angle) ) ) )
										//map to inch estimate
										//18 is estimated waist WIDTH (ie inches from side to side)
										.map( d => Math.sin(d/180*Math.PI)*18 )
									).map( d => ({
										v:`${d.toFixed(1)}"`,
										color:d<=1?'green':'red'
									}) )
								)
							},{
								metric:'Right Max Hip Drop',
								values:(
									appendAvg(
										extractRepSlices(results.right.reps,results.right.hipTilt)
										.map( d => Math.max( ...d.map( d => Math.abs(d.angle) ) ) )
										//18 is estimated waist WIDTH (ie inches from side to side)
										//map to inch estimate
										.map( d => Math.sin(d/180*Math.PI)*18 )
									).map( d => ({
										v:`${d.toFixed(1)}"`,
										color:d<=1?'green':'red'
									}) )
								)
							},{
								metric:'Left Knee Deviation',
								values:(
									appendAvg(
										extractRepSlices(results.left.reps,results.left.valgus)
										.map( d => Math.max( ...d.map( d => d.valgus/guidesRef.current.height ) ) )
									)
									//69 is estimated height in inches
									.map( d => ({v:`${Math.abs(d*69).toFixed(2)}" (${d<=0?'varus':'valgus'})`,color:d<=0?'green':'red'}) )
								)
							},{
								metric:'Right Knee Deviation',
								values:(
									appendAvg(
										extractRepSlices(results.right.reps,results.right.valgus)
										.map( d => Math.max( ...d.map( d => d.valgus/guidesRef.current.height ) ) )
									)
									//69 is estimated height in inches
									.map( d => ({v:`${Math.abs(d*69).toFixed(2)}" (${d<=0?'varus':'valgus'})`,color:d<=0?'green':'red'}) )
								)
							},{
								metric:'Hands Off Hips Count (Left)',
								values:(
									appendAvg(
										countPerRepHandsOffHips(results.left.reps,results.left.handsOffHips)
									).map(
										c=>({
											v:c.toFixed(1),
											color:c>0?'red':'green'
										})
									)
								)
							},{
								metric:'Hands Off Hips Count (Right)',
								values:(
									appendAvg(
										countPerRepHandsOffHips(results.right.reps,results.right.handsOffHips)
									).map(
										c=>({
											v:c.toFixed(1),
											color:c>0?'red':'green'
										})
									)
								)
							}
						]}
					/>
					<KeypointLogViewer 
						id="single-leg-squat"
						demo="single-leg-squat"
						results={results}
					/>
					<Button 
						onClick={handleAddToProgress}
						variant="outline-success" 
						className="my-3"
						block
					>
						Add to {isClient?'':"Client's"} Progress Metrics
					</Button>
					{Object.entries({left:results.left,right:results.right}).map(([side,data])=>(
						<>
							<div style={{
								fontSize:'24px',
								textAlign:'center',
								margin:'16px 0'
							}}>
								{console.log(data)}
								{capitalize(side)} Side
							</div>
							
							<div style={{width:'100%',height:'200px'}}>
								<Line
									options={{
										maintainAspectRatio:false,
										responsive:true,
										animation: {
											duration: 0
										},
										hover: {
											mode: 'nearest'
										},
										scales: {
											xAxes: [{
												ticks:{
													callback:(label)=>(label/1000).toFixed(1),
													maxTicksLimit:10
												},
												display:true,
												scaleLabel:{
													display:true,
													labelString:'Time (seconds)'
												}
											}],//end xAxes
											yAxes: [{
												display:true,
												scaleLabel:{
													display:true,
													labelString:'Chest Height'
												}
											}]//end yAxes
										}//end scales
									}}
									data={
										{
											labels:data.chestPosition.map((p)=>p.t - data.chestPosition[0].t),
											datasets: [
												{
													lineTension: 0,
													label: 'Success Threshold - 30deg',
													data:data.chestPosition.map(p=>({
														x:p.t - data.chestPosition[0].t,
														y:roundDecimal(320 - guidesRef.current.idealChestY,2)
													})),
													backgroundColor: "rgba(87, 189, 81,0.2)",
													borderColor: "rgba(87, 189, 81,1)",
													pointRadius: 0,
												},{
													lineTension: 0,
													label: 'Chest Height',
													data:data.chestPosition.map(p=>({
														x:p.t - data.chestPosition[0].t,
														y:roundDecimal(320 - p.y - guidesRef.current.squatStartY,2),
														status:p.status
													})),
													backgroundColor: "rgba(75,192,192,0.2)",
													borderColor: "rgba(73, 197, 235,1)"
												},{
													showLine:false,
													lineTension: 0,
													label: 'Rep Started',
													fill:false,
													data:data.reps.started.map(t=>({
														x:t - data.chestPosition[0].t,
														y:roundDecimal(320 - guidesRef.current.squatStartY + 5,2)
													})),
													borderColor: "rgba(52, 168, 114,1)",
													backgroundColor:'rgba(52, 168, 114,0.2)'
												},{
													showLine:false,
													lineTension: 0,
													label: 'Rep Finished',
													fill:false,
													data:data.reps.ended.map(t=>({
														x:t - data.chestPosition[0].t,
														y:roundDecimal(320 - guidesRef.current.squatStartY + 5)
													})),
													borderColor: "rgba(230, 137, 16,1)",
													backgroundColor:'rgba(230, 137, 16,0.2)'
												}
											]
										}
									}
								/>
							</div>
							<div style={{width:'100%',height:'200px'}}>
								<Line
									options={{
										maintainAspectRatio:false,
										responsive:true,
										animation: {
											duration: 0
										},
										scales: {
											xAxes: [{
												ticks:{
													callback:(label)=>(label/1000).toFixed(1),
													maxTicksLimit:10
												},
												display:true,
												scaleLabel:{
													display:true,
													labelString:'Time (seconds)'
												}
											}],//end xAxes
											yAxes: [{
												display:true,
												scaleLabel:{
													display:true,
													labelString:'Varus     ( inches )     Valgus'
												},
												ticks:{
													suggestedMin:32,
													suggestedMax:32
												},
											}]//end yAxes
										}//end scales
									}}
									data={
										{
											labels:data.chestPosition.map((p)=>p.t - data.chestPosition[0].t),
											datasets: [
												{
													lineTension: 0,
													label:`${capitalize(side)} Knee`,
													data:data.valgus.map(p=>({
														x:p.t ,
														y:roundDecimal(p.valgus*100/guidesRef.current.height,2)
													})),
													pointBackgroundColor:function(context){
														const index = context.dataIndex
														const pt = context.dataset.data[index]
														
														return (
															pt.y>0
															? "rgba(255,50,50,1)"
															: "transparent"
														)
														
													},
													backgroundColor: "rgba(172, 113, 235,0.2)",
													borderColor: "rgba(172, 113, 235,1)"
												}
											]
										}
									}
								/>
							</div>
							<div style={{width:'100%',height:'200px'}}>
								<Line
									options={{
										maintainAspectRatio:false,
										responsive:true,
										animation: {
											duration: 0
										},
										hover: {
											mode: 'nearest'
										},
										scales: {
											xAxes: [{
												ticks:{
													callback:(label)=>(label/1000).toFixed(1),
													maxTicksLimit:10
												},
												display:true,
												scaleLabel:{
													display:true,
													labelString:'Time (seconds)'
												}
											}],//end xAxes
											yAxes: [
												{
													display:true,
													position:'top',
													ticks:{
														suggestedMin:15,
														suggestedMax:15
													},
													scaleLabel:{
														display:true,
														labelString:'Tilt Angle (degrees)'
													}
												}
											]//end yAxes
										}//end scales
									}}
									data={
										{
											labels:data.hipTilt.map((p)=>p.t - data.hipTilt[0].t),
											datasets: [
												{
													lineTension: 0,
													label: 'Hip Drop',
													data:data.hipTilt.map(p=>({
														x:p.t - data.hipTilt[0].t,
														y:roundDecimal(p.angle),
													})),
													backgroundColor: "rgba(96, 191, 108,0.2)",
													borderColor: "rgba(96, 191, 108,1)"
												}
											]
										}
									}
								/>
							</div>
							<div className="d-flex flex-wrap justify-content-between mt-3">
								{extractRepSlices(data.reps,data.chestPosition).map((data,i)=>(
									<div style={{width:'180px',height:'500px'}}>
										<Scatter
											options={{
												maintainAspectRatio:false,
												responsive:true,
												animation: {
													duration: 0
												},
												title:{
													display:true,
													text:`Rep ${i+1} center`,
												},
												scales: {
													xAxes: [{
														display:true,
														scaleLabel:{
															display:true,
															labelString:'Center Deviation'
														}
													}],//end xAxes
													yAxes: [{
														display:true,
														scaleLabel:{
															display:true,
															labelString:'Squat Height'
														}
													}]//end yAxes
												}//end scales
											}}
											data={{
												datasets:[
													{
														label:`Down`,
														data:normalizeX(
															data.slice(0,argMax(data.map(p=>p.y)))
														).map(p=>({x:p.x-0.5,y:roundDecimal(p.y)})),
														backgroundColor: 'rgb(3, 127, 252)'
													},{
														label:`Up`,
														data:normalizeX(
															data.slice(argMax(data.map(p=>p.y)))
														).map(p=>({x:p.x-0.5,y:roundDecimal(p.y)})),
														backgroundColor: 'rgb(127, 252, 3)'
													},
												]
											}}
										/>
									</div>
								))}
								{/*
								<div>
									<div 
										className="d-flex flex-column align-items-stretch" 
										style={{flex:'0 0 auto',width:'400px',maxWidth:'100%'}}
									>
										<div className="d-flex justify-content-between">
											<strong>Metric</strong>
											<strong>Score</strong>
										</div>
										<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
											<div className="mr-2">Depth Score</div>
											<div>{depthScoreByKneeAngle(data.reps,data.kneeAngle).toFixed()} %</div>
										</div>
									
										<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
											<div className="mr-2">Balance</div>
											<div>{singleLegSquatBalanceScore(data.reps,data.handsOffHips).toFixed()} %</div>
										</div>
										<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
											<div className="mr-2">Knee Movement</div>
											<div>
												{singleLegKneeScore(results[side].reps,results[side].valgus).toFixed()} %
											</div>
										</div>
										<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
											<div className="mr-2">Hip Drop</div>
											<div>
												{tiltScore(
													data.hipTilt.map(p=>({
														x:p.t - data.hipTilt[0].t,
														y:p.angle,
														status:p.status
													}))
												).toFixed(0)} %
											</div>
										</div>
										<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
											<div className="mr-2">
												<strong>Final Score</strong>
											</div>
											<div>
												<strong>
													{averageScore(side).toFixed()} %
												</strong>
											</div>
										</div>
									</div>
								</div>
								*/}
							</div>
						</>
					))}
					{(parseInt(user.user_id_number) === 100017099 || window.location.hostname === 'localhost')&&
						<>
							<strong className="mt-2">Name</strong>
							<Form.Control 
								onChange={e=>{
									setName(e.target.value)
								}}
								value={name} 
								placeholder="name"
							/>
							<strong>Email</strong>
							<Form.Control 
								onChange={e=>{
									setEmail(e.target.value)
								}}
								value={email}
								placeholder="email"
							/>
							<Button
								disabled={!Boolean(name)||saving}
								onClick={handleSave}
								variant="outline-success"
								className="mt-2"
								block
							>
								Add to Leaderboard {saving&&<Spinner size="sm" animation="border"/>}
							</Button>
						</>
					}
					<div className="mt-5 d-flex">
						<Button 
							className="mr-2"
							onClick={restartDemo}
							variant="outline-dark"
						>
							Restart
						</Button>
						{!isWebview()&&
							<Button 
								onClick={()=>{
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
					</div>
				</div>
			}
			{(parseInt(user.user_id_number) === 100017099 || window.location.hostname === 'localhost')&&
				<div style={{
					position:'absolute',
					top:0,left:0,
					transform:`translateX(${showLeaderboard?'0':'-100%'})`, 
					transition:'0.2s',
					width:'600px',
					maxWidth:'100%',
					height:'100vh',
					zIndex:6,
					boxShadow:showLeaderboard?'0 0 16px rgba(0,0,0,0.6)':'none'
				}}>
					<button 
						onClick={()=>{
							setShowLeaderboard( p => !p )
						}}
						style={{
							position:'absolute',
							top:'80px',right:0,
							transform:'translateX(100%)',
							padding:'16px',
							background:'#fff',
							border:'none',
							zIndex:5,
							boxShadow:'0 0 16px rgba(0,0,0,0.2)',
						}}
					>
						Leaderboard
					</button>
					<div style={{
						width:'100%',
						height:'100%',
						overflow:'auto',
						position:'absolute',
						background:'#fff',
						padding:'8px',
						top:0,left:0,
						zIndex:6
					}}>
						<Leaderboard/>
					</div>
				</div>
			}
			{showHowTo&&
				<HowTo onHide={()=>{setShowHowTo(false)}}>
					<strong>What is this?</strong>
					<p>
						With only a webcam, our system will automatically score your single leg squat form. 
					</p>
					<strong>How do I use this?</strong>
					<ul>
						<li><span className="mr-2">1.</span>Press the green <span id={styles.StartHowTo}>Start</span> button & <strong>wait</strong> till it turns into a red <span id={styles.StopHowTo}>Stop</span> button (the first time always takes a few seconds)</li>
						<li><span className="mr-2">2.</span>Step back until your <strong>full body is in view</strong> of the camera and make sure to <strong>face the camera straight on</strong></li>
						<li><span className="mr-2">3.</span>When in position place your hands behind your head to signal the system you are ready to begin.</li>
						<li><span className="mr-2">4.</span>When ready, <strong>place your hands on your hips and lift one foot</strong> then a <strong>3 second countdown</strong> will appear</li>
						<li><span className="mr-2">5.</span>When you see <span className="text-success"><strong>GO!</strong></span> do <strong>3 single leg squats</strong> on <strong>each side</strong></li>
						<li><span className="mr-2">6.</span>View your results and have fun trying to get a better score!</li>
						<li><span className="mr-2">7.</span>If the <span id={styles.RestartHowTo}>Restart</span> button is not in view <strong>scroll down</strong></li>
					</ul>
					<div className="d-flex align-items-end flex-wrap justify-content-around">
						<a target="_blank" href="https://www.youtube.com/watch?v=rPjhkwmExEY">Check out a live demo</a>
						<div className="text-center">
							<div><small>For more more info read the</small></div>
							<a target="_blank" href="https://www.fitsw.com/blog/exercise-motion-analysis-ai/">Blog Post About fitsw.ai</a>
						</div>
					</div>
				</HowTo>
			}
		</div>
	)
}


export default SingleLegSquatDemo